import { artifactIdentityKey, findArtifactRecord, mergeArtifactRecords, pickArtifactSummary, type ArtifactMergeLike } from './chatArtifactMerge';

export interface ArtifactTimelineEventLike {
  id: string;
  type?: string;
  artifactId?: string;
  kind?: string;
  summary?: string;
  size?: number;
  mimeType?: string;
  timestamp?: string;
  runId?: string;
}

export interface ArtifactMessageLike<TArtifact extends ArtifactMergeLike> {
  id: string;
  role: 'assistant' | 'user';
  runId?: string;
  timestamp?: string;
  artifacts?: TArtifact[];
}

export interface BuildArtifactCollectionsArgs<TArtifact extends ArtifactMergeLike> {
  artifactsByRun: Record<string, TArtifact[]>;
  currentRunId?: string | null;
  currentRunArtifacts?: TArtifact[];
  runEventHistory: Record<string, ArtifactTimelineEventLike[]>;
  messageEvents: ArtifactMessageLike<TArtifact>[];
  fallbackArtifactRunByMessageId: Record<string, string | undefined>;
}

export interface ArtifactCollections<TArtifact extends ArtifactMergeLike> {
  allArtifacts: TArtifact[];
  finalArtifacts: TArtifact[];
  intermediateArtifacts: TArtifact[];
}

export const buildArtifactCollections = <TArtifact extends ArtifactMergeLike>({
  artifactsByRun,
  currentRunId,
  currentRunArtifacts = [],
  runEventHistory,
  messageEvents,
  fallbackArtifactRunByMessageId
}: BuildArtifactCollectionsArgs<TArtifact>): ArtifactCollections<TArtifact> => {
  const collected = new Map<string, TArtifact>();

  const upsertArtifact = (artifact?: TArtifact | null) => {
    if (!artifact?.id) {
      return;
    }
    const key = artifactIdentityKey(artifact.id, artifact.runId);
    collected.set(key, mergeArtifactRecords(collected.get(key), artifact));
  };

  Object.entries(artifactsByRun).forEach(([runId, list]) =>
    list.forEach((artifact) =>
      upsertArtifact({
        ...artifact,
        runId: artifact.runId ?? runId
      } as TArtifact)
    )
  );
  currentRunArtifacts.forEach((artifact) =>
    upsertArtifact({
      ...artifact,
      runId: artifact.runId ?? currentRunId ?? undefined
    } as TArtifact)
  );
  Object.entries(runEventHistory).forEach(([runId, events]) => {
    events.forEach((event) => {
      if (event.type !== 'artifact') {
        return;
      }
      const artifactId = event.artifactId ?? event.id;
      if (!artifactId) {
        return;
      }
      upsertArtifact({
        id: artifactId,
        kind: event.kind,
        summary: event.summary,
        size: event.size,
        mimeType: event.mimeType,
        runId: event.runId ?? runId,
        createdAt: event.timestamp
      } as TArtifact);
    });
  });
  messageEvents.forEach((event) => {
    const fallbackRunId = fallbackArtifactRunByMessageId[event.id];
    event.artifacts?.forEach((artifact) =>
      upsertArtifact({
        ...artifact,
        runId: artifact.runId ?? event.runId ?? fallbackRunId,
        createdAt: artifact.createdAt ?? event.timestamp
      } as TArtifact)
    );
  });

  const allArtifacts = Array.from(collected.values());

  const finalArtifactMap = new Map<string, TArtifact>();
  messageEvents.forEach((event) => {
    if (event.role !== 'assistant') {
      return;
    }
    const fallbackRunId = fallbackArtifactRunByMessageId[event.id];
    const messageArtifacts =
      event.artifacts && event.artifacts.length > 0
        ? event.artifacts
        : fallbackRunId
          ? artifactsByRun[fallbackRunId] ?? []
          : [];

    messageArtifacts.forEach((artifact) => {
      const artifactRunId = artifact.runId ?? event.runId ?? fallbackRunId;
      const key = artifactIdentityKey(artifact.id, artifactRunId);
      if (!artifact.id || finalArtifactMap.has(key)) {
        return;
      }
      const enriched = findArtifactRecord(allArtifacts, artifact.id, artifactRunId);
      finalArtifactMap.set(key, {
        ...(enriched ?? artifact),
        runId: artifactRunId ?? enriched?.runId,
        summary: pickArtifactSummary(artifact.summary ?? undefined, enriched?.summary ?? undefined),
        evidence: artifact.evidence ?? enriched?.evidence,
        isFinalAttachment: true
      } as TArtifact);
    });
  });

  const finalArtifacts = Array.from(finalArtifactMap.values());
  const finalArtifactIds = new Set(finalArtifacts.map((artifact) => artifactIdentityKey(artifact.id, artifact.runId)));
  const intermediateArtifacts = allArtifacts.filter(
    (artifact) => artifact.id && !finalArtifactIds.has(artifactIdentityKey(artifact.id, artifact.runId))
  );

  return {
    allArtifacts,
    finalArtifacts,
    intermediateArtifacts
  };
};
